// Pure helpers for the client-since issue's decide flow (Nate
// 2026-07-02). The hcr-missing-client-since recommendation says "set
// the client-since date" — but the rail's old "review" verb was a
// dead end ("open the record to apply it") and the record page showed
// the field read-only, so Nate ended up putting the date in a contact
// note. The rail now renders a date input and sends
// params.client_since_date; the backend writes it onto the household's
// company relationship (audited, 60s-undoable) and resolves the issue.
//
// Same pattern + conventions as expiration-issue.ts (Joy 2026-07-01).
// Root-safe (imports nothing) so it's unit-tested from tests/web/.

// Minimal structural subset of CleanupIssueSummary this module reads.
export interface ClientSinceIssueLike {
  detectorSlug: string;
  owningCompanySlug?: string | null;
}

// Issues whose "verify" carries a client-since date to the backend.
export function isClientSinceIssue(issue: ClientSinceIssueLike): boolean {
  return issue.detectorSlug === "hcr-missing-client-since";
}

// Mirror of shared/lib/iso-date.ts (kept dependency-free so this module
// stays root-safe): strict YYYY-MM-DD that round-trips through Date.
export function isValidClientSinceInput(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00.000Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

// Plain-language before→after for the date-carrying verify (UX rule
// #3). Entering the date IS the action — the backend 400s without it.
//
// Grant 2026-07-10 QA: "Annual Review cycle" only applies to Financial
// (schema/migrations/0003 — Insurance's Renewal Plan anchors on the
// policy's own effective_date, not client_since). Only claim it here
// when the issue is actually Financial's.
//
// Grant 2026-07-12 (residue rulings): `isFloor` = the advisor ticked
// "earliest I know of" — the date is stored as a FLOOR (migration 0200)
// and renders as "client since at least <date>" everywhere.
export function clientSinceConsequence(
  issue: ClientSinceIssueLike,
  date: string,
  isFloor = false,
): string | null {
  if (!isClientSinceIssue(issue)) return null;
  const anchorsReviewCycle = issue.owningCompanySlug === "financial";
  if (date && isValidClientSinceInput(date)) {
    if (isFloor) {
      return `Records that this household has been a client since AT LEAST ${date} ("earliest you know of") on the company relationship and resolves this issue — it renders as "client since at least ${date}" until someone confirms the exact date. Audited; reversible for 60 seconds.`;
    }
    return `Sets this household's client-since date to ${date} on the company relationship and resolves this issue. Tenure${
      anchorsReviewCycle ? ", the Annual Review cycle," : ""
    } and first-client-year reporting anchor on it. Audited; reversible for 60 seconds.`;
  }
  return "Enter the date the relationship began first — setting it is the whole action here. Use the original agreement / first policy date; only sure of the earliest date? Tick “earliest I know of”. Genuinely unknown? Use “We don't know when they became a client” below.";
}

// Copy for the "we don't know" escape button + its consequence line
// (Grant 2026-07-12 — "if they do not know they need to have an option
// that allows it"). Same terminal-answer pattern as the PM entity
// "not known" and the What-Matters-Most don't-know escape.
export const CLIENT_SINCE_UNKNOWN_LABEL =
  "We don't know when they became a client";

export function clientSinceUnknownConsequence(): string {
  return "Records that nobody knows when this household became a client of this company — the item resolves for the whole team and it will not be asked again. If a real date ever surfaces, setting it clears this answer. Reversible for 60 seconds (undo clears the answer and reopens the item).";
}
